import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import PlanningApp from './PlanningApp';
import PlanningParametrage from './PlanningParametrage';
import PlanningExportCalendrier from './PlanningExportCalendrier';
import PlanningExportEvenements from './PlanningExportEvenements';
import PlanningAccueil from './PlanningAccueil';
import {
    ETAPES, ONGLETS, ALIAS_ONGLETS, estEtapeParametrage, premiereAFaire, PlanningEtapesContext,
} from './PlanningEtapes';
import { planningAPI } from '../services/api';
import { peutModifier } from '../services/droits';
// PA.4 (règle 9) : l'aide contextuelle commune — le « ? » du bandeau, son volet, la fenêtre des processus, le signalement.
import { AideProvider, useAide } from './aide/AideContexte';
import VoletAide from './aide/VoletAide';
import FenetreProcessus from './aide/FenetreProcessus';
import SignalerManque from './aide/SignalerManque';
import AIDE_PLANNING from '../aide/planning';

/** La racine du module : l'écran sous le bandeau se décale de la largeur du volet d'aide quand il est ouvert. */
function RacinePlanning({ children }) {
    const aide = useAide();
    return <div className={'pl-module' + (aide && aide.ouvert ? ' pl-aide-ouverte' : '')}>{children}</div>;
}

/**
 * Module Planning annuel — le conteneur qui porte les deux onglets (lot 5.4).
 *
 * POURQUOI CE COMPOSANT EXISTE.
 * Calendrier et Paramétrage étaient deux ROUTES (`/planning` et `/setup`) déguisées
 * en deux onglets d'une même barre. Le passage de l'une à l'autre n'était pas une
 * navigation, c'était une destruction suivie d'une reconstruction :
 *
 *   Calendrier → Paramétrage  `handleBackToSetup` faisait
 *                             `localStorage.removeItem('planningConfig')` puis
 *                             `setConfig(null)` — la configuration chargée était jetée
 *   Paramétrage → Calendrier  `handleAllerCalendrier` RÉ-ENREGISTRAIT toute la
 *                             configuration et la reconstruisait de zéro, en
 *                             demandant au passage, dans un `window.confirm`, quoi
 *                             faire des modifications en cours
 *
 * Deux onglets voisins, et cliquer de l'un à l'autre jetait l'état. C'est aussi ce
 * qui rendait nécessaire le dernier `window.confirm` du module : la question
 * « sauvegarder avant de partir ? » n'a de sens que si partir détruit quelque chose.
 * Ici, changer d'onglet ne fait que changer ce qui est affiché — la question ne se
 * pose plus, et la modale disparaît avec elle.
 *
 * L'ONGLET EST DANS L'URL (`?onglet=parametrage`) : le rechargement de la page, le
 * bouton Précédent du navigateur et le lien envoyé à un collègue retombent sur
 * l'écran attendu. C'est aussi ce qui permet à l'ancienne route `/setup` de survivre
 * en simple redirection — rien n'a été supprimé avant d'avoir son remplacement.
 *
 * SANS CONFIGURATION, on ouvre le Paramétrage : c'est précisément l'écran qui sert à
 * en créer une. `/planning` ne renvoie donc plus vers `/setup` — la redirection
 * croisée entre les deux serait devenue une boucle.
 *
 * LOT PA.1 (revue ergonomique, octobre 2026) — le module s'ouvre sur un ACCUEIL à
 * vignettes, comme le Stage et le Planning oraux (`/planning` sans `?onglet=`). La
 * vignette « Planning annuel » ouvre la PREMIÈRE ÉTAPE À FAIRE, le Calendrier quand
 * tout est fait. Le Paramétrage est coupé en trois étapes (`etape1` · `etape2` ·
 * `etape3`) ; `?onglet=parametrage` reste compris (→ étape 1). Sans configuration,
 * l'accueil et les étapes 1 à 3 restent ouverts, le reste est grisé avec son motif.
 */
/** Ce qu'il faut pour afficher l'onglet : une configuration, ou rien. */
const exigeConfig = (cle) => !!ETAPES.find((e) => e.cle === cle)?.exigeConfig;

function PlanningModule({
    token,
    user,
    config,
    onConfigChange,
    onLogout,
    initialDept,
    anneeDebut,
    anneeId,
    onAnneeChange,
}) {
    const [params, setParams] = useSearchParams();
    // La liste est explicite plutôt qu'un test sur une seule valeur — un paramètre
    // inconnu retombe sur l'accueil (lot PA.1 ; c'était le Calendrier).
    const brut = ALIAS_ONGLETS[params.get('onglet')] || params.get('onglet');
    const ongletUrl = ONGLETS.includes(brut) ? brut : 'accueil';

    // Sans configuration, le Calendrier et les exports n'ont rien à afficher : on
    // reste sur l'accueil ou sur les étapes 1 à 3, quel que soit ce que dit l'URL.
    const [onglet, setOnglet] = useState(config || !exigeConfig(ongletUrl) ? ongletUrl : 'accueil');

    /* ═══ CE QUE DISAIT L'URL EN ARRIVANT ════════════════════════════════════
       Défaut trouvé en codant le lot 5.6 bis, corrigé ici : ouvrir
       `/planning?onglet=export` — un signet, un lien envoyé à un collègue, ou
       simplement RECHARGER la page en étant sur l'export — atterrissait sur le
       Paramétrage.

       L'enchaînement : `config` arrive de façon asynchrone (`useState(null)` dans
       App.js), donc au premier rendu il est absent et l'onglet retombe sur
       Paramétrage — c'est voulu. Mais l'effet ci-dessous, qui fait suivre l'URL à
       l'onglet, RÉÉCRIVAIT alors l'URL en `?onglet=parametrage` : quand la
       configuration arrivait, l'intention de départ avait été effacée par
       l'application elle-même, et plus rien ne pouvait la retrouver.

       On retient donc l'onglet demandé À L'ARRIVÉE, une fois pour toutes, et on y
       revient dès que la configuration est là. Le repère `aligne` empêche de
       ramener l'utilisateur sur cet onglet à chaque changement de configuration :
       ça ne joue qu'une fois par entrée dans le module. */
    const ongletDemande = useRef(ongletUrl);
    const aligne = useRef(!!config);

    useEffect(() => {
        if (!config) {
            aligne.current = false;
            if (exigeConfig(onglet)) setOnglet('accueil');
            return;
        }
        if (!aligne.current) {
            aligne.current = true;
            setOnglet(ongletDemande.current);
        }
    }, [config, onglet]);

    // L'URL suit l'onglet, sans empiler d'entrée dans l'historique à chaque clic
    // (`replace`) : le bouton Précédent doit sortir du module, pas rejouer les
    // allers-retours entre deux onglets.
    useEffect(() => {
        const voulu = onglet === 'accueil' ? null : onglet;
        const actuel = params.get('onglet');
        if (voulu === actuel) return;
        const suivant = new URLSearchParams(params);
        if (voulu) suivant.set('onglet', voulu); else suivant.delete('onglet');
        setParams(suivant, { replace: true });
    }, [onglet, params, setParams]);

    const changerOnglet = useCallback((cle) => {
        // Le Calendrier ET les exports ont besoin d'une configuration : l'un affiche
        // les colonnes, les autres les impriment. Sans elle, seuls l'accueil et les
        // étapes 1 à 3 ont quelque chose à montrer.
        const c = ALIAS_ONGLETS[cle] || cle;
        if (exigeConfig(c) && !config) return;
        // Un choix de l'utilisateur remplace l'intention de l'URL d'arrivée : sans ça,
        // la PREMIÈRE configuration enregistrée (étape 3) le renverrait sur l'onglet
        // de l'URL, au lieu de le laisser où il est.
        ongletDemande.current = c;
        setOnglet(c);
    }, [config]);

    /* ═══ L'ÉTAT DES ÉTAPES (lot PA.1) ═══════════════════════════════════════
       Relu à chaque changement d'onglet : c'est en quittant une étape qu'on veut
       voir son numéro passer au vert. Un échec de lecture ne bloque rien — la
       barre reste sans couleurs, l'accueil dit qu'il n'a pas pu lire l'état. */
    const departementId = config?.departement?.id || initialDept?.id || user?.departements?.[0]?.id || null;
    const [etat, setEtat] = useState(null);
    const [erreurEtat, setErreurEtat] = useState('');
    const rafraichir = useCallback(async () => {
        if (!departementId || !anneeId) { setEtat(null); return; }
        try {
            const { data } = await planningAPI.getAccueil(departementId, anneeId);
            setEtat(data);
            setErreurEtat('');
        } catch (err) {
            setErreurEtat(err?.response?.data?.error || err?.message || 'erreur inconnue');
        }
    }, [departementId, anneeId]);
    useEffect(() => { rafraichir(); }, [rafraichir, onglet]);

    const anneeLibelle = anneeDebut ? `${anneeDebut}-${anneeDebut + 1}` : '';
    const bandeau = useRef(null);
    const contexte = useMemo(() => ({
        etat, anneeLibelle, configPrete: !!config, rafraichir, bandeau,
    }), [etat, anneeLibelle, config, rafraichir]);

    // La vignette « Planning annuel » : la première étape à faire, sinon le Calendrier.
    // Sans configuration, le Calendrier n'est pas ouvrable : l'étape 3 la crée.
    const ouvrirPlanning = useCallback(() => {
        const cible = premiereAFaire(etat);
        changerOnglet(exigeConfig(cible) && !config ? 'etape3' : cible);
    }, [etat, config, changerOnglet]);

    /**
     * Le Paramétrage vient d'enregistrer : la configuration du module change
     * (colonnes, types de journées, heures). On la remonte à `App.js`, qui la
     * détient pour toute l'application — mais SANS NAVIGUER. C'est toute la
     * différence avec l'ancien `onComplete`, dont le nom disait bien qu'il
     * terminait un assistant.
     */
    const handleConfigEnregistree = useCallback((nouvelleConfig) => {
        onConfigChange(nouvelleConfig);
    }, [onConfigChange]);

    // Lot PA.2 — une vignette de modèle ouvre l'export, ses réglages chargés ; « Télécharger
    // le PDF » fait de même et télécharge. Le PDF reste fabriqué par l'écran d'export : un
    // seul générateur, pas une copie de plus. `jeton` : deux clics sur la même vignette
    // sont deux demandes.
    const [modeleDemande, setModeleDemande] = useState(null);
    // `action` : rien (ouvrir), 'telecharger' ou 'envoyer' (lot PA.3 : la fenêtre d'envoi s'ouvre sur l'export)
    const ouvrirModele = useCallback((m, action) => {
        setModeleDemande({ id: m.id, telecharger: action === 'telecharger', envoyer: action === 'envoyer', jeton: Date.now() });
        changerOnglet(m.type === 'evenements' ? 'exportevt' : 'export');
    }, [changerOnglet]);
    const modeleTraite = useCallback(() => setModeleDemande(null), []);
    const peutEcrire = !!departementId && peutModifier(user, departementId, 'planning');

    /* ═══ L'AIDE (lot PA.4) ══════════════════════════════════════════════════
       La clé d'écran est l'onglet (`accueil`, `etape1`…, les valeurs de `?onglet=`) ;
       la fenêtre « Préparer à partir de … » s'annonce elle-même (`accueil/preparer`).
       Un écran que l'aide ne peut pas ouvrir d'ici reste visible avec sa raison. */
    const ecranDemande = useLocation().state?.aideEcran || null;
    const accessibleAide = useCallback((cle) => {
        if (exigeConfig(cle) && !config) return 'enregistrez d\'abord les formations (étape 3)';
        if (cle === 'accueil/preparer') {
            if (!etat?.vide || !etat?.preparerDepuis) return 'seulement pour une année encore vide, quand une année précédente a un planning';
            if (!peutEcrire) return 'réservé à qui peut modifier le module';
        }
        return null;
    }, [config, etat, peutEcrire]);
    // `accueil/preparer` : l'accueil s'ouvre, puis il ouvre lui-même la fenêtre (useDemandeEcran).
    const ouvrirDepuisAide = useCallback((cle) => changerOnglet(cle.split('/')[0]), [changerOnglet]);

    const ecran = (() => {
        if (onglet === 'accueil') {
            return (
                <PlanningAccueil
                    config={config}
                    departementId={departementId}
                    anneeId={anneeId}
                    peutEcrire={peutEcrire}
                    onOuvrirModele={ouvrirModele}
                    onRafraichir={rafraichir}
                    etat={etat}
                    erreurEtat={erreurEtat}
                    anneeLibelle={anneeLibelle}
                    sousTitre={(config?.departement?.nom || initialDept?.nom || '') + (anneeLibelle ? ` · ${anneeLibelle}` : '')}
                    onChangeOnglet={changerOnglet}
                    onOuvrirPlanning={ouvrirPlanning}
                />
            );
        }
        // Les trois étapes du Paramétrage rendent le MÊME composant à la même place :
        // passer de l'une à l'autre garde la saisie en cours (React ne le recrée pas).
        if (estEtapeParametrage(onglet)) {
            return (
                <PlanningParametrage
                    user={user}
                    config={config}
                    initialDept={initialDept}
                    externalAnneeDebut={anneeDebut}
                    onglet={onglet}
                    onChangeOnglet={changerOnglet}
                    calendrierPret={!!config}
                    onConfigEnregistree={handleConfigEnregistree}
                    onEnregistre={rafraichir}
                    onLogout={onLogout}
                />
            );
        }

        if (onglet === 'export') {
            return (
                <PlanningExportCalendrier
                    user={user}
                    config={config}
                    onglet={onglet}
                    onChangeOnglet={changerOnglet}
                    anneeId={anneeId}
                    modeleDemande={modeleDemande}
                    onModeleDemandeTraite={modeleTraite}
                />
            );
        }

        if (onglet === 'exportevt') {
            return (
                <PlanningExportEvenements
                    user={user}
                    config={config}
                    onglet={onglet}
                    onChangeOnglet={changerOnglet}
                    anneeId={anneeId}
                    modeleDemande={modeleDemande}
                    onModeleDemandeTraite={modeleTraite}
                />
            );
        }

        return (
            <PlanningApp
                token={token}
                user={user}
                config={config}
                onLogout={onLogout}
                onglet={onglet}
                onChangeOnglet={changerOnglet}
                anneeId={anneeId}
                onAnneeChange={onAnneeChange}
            />
        );
    })();

    return (
        <PlanningEtapesContext.Provider value={contexte}>
            <AideProvider donnees={AIDE_PLANNING} base={onglet} departementId={departementId} onOuvrir={ouvrirDepuisAide}
                ouvrirSur={ecranDemande} accessible={accessibleAide}>
                <RacinePlanning>
                    {ecran}
                    <VoletAide ancre={bandeau} />
                    <FenetreProcessus />
                    <SignalerManque />
                </RacinePlanning>
            </AideProvider>
        </PlanningEtapesContext.Provider>
    );
}

export default PlanningModule;
